import { useCallback, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "@/services/store";
import { useGetAllPersonsQuery } from "@/services/api";
import { appRouterPaths } from "@/services/navigation";
import type { MaybeType } from "@/shared/types";
import type { ContactGroupFilter } from "@/entities/contacts-filters/model";
import { contactsActions, contactsSelectors } from "../slice";

export const useContacts = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { personId } = useParams();

  const { data } = useGetAllPersonsQuery();

  const groupFilter = useAppSelector(contactsSelectors.selectGroupFilter);
  const selectedPersonId = useAppSelector(contactsSelectors.selectSelectedPersonId);

  useEffect(() => {
    if (selectedPersonId && !personId) {
      navigate(`${appRouterPaths.contacts.genInfo.replace(":personId", selectedPersonId)}`);
    }
  }, [selectedPersonId]);

  const setGroupFilter = useCallback(
    (filter: ContactGroupFilter) => {
      dispatch(contactsActions.setGroupFilter(filter));
    },
    [dispatch],
  );

  const setSelectedPersonId = useCallback(
    (id: MaybeType<string>) => {
      dispatch(contactsActions.setSelectedPersonId(id));
    },
    [dispatch],
  );

  const filteredContacts = useMemo(() => {
    if (!groupFilter) return data?.persons;
    return data?.persons.filter((person) => person.group === groupFilter);
  }, [data?.persons, groupFilter]);

  return {
    state: {
      filteredContacts: filteredContacts ?? [],
      selectedPersonId,
      groupFilter,
    },
    methods: {
      setGroupFilter,
      setSelectedPersonId,
    },
  };
};
